import { motion } from "framer-motion";
import { Trophy, TrendingDown, DollarSign, TrendingUp } from "lucide-react";
import { formatEur, formatPct } from "@/lib/index";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface WinnerBannerProps {
  netP1: number;
  netP2: number;
  savingsPct: number;
  savingsEur: number;
  p1Contribution: number;
  p2Contribution: number;
  furnitureCost: number;
  billingBenefit: number;
}

export function WinnerBanner({
  netP1,
  netP2,
  savingsPct,
  savingsEur,
  p1Contribution,
  p2Contribution,
  furnitureCost,
  billingBenefit
}: WinnerBannerProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 35 }}
    >
      <Card className="relative overflow-hidden border-2 border-success/30">
        <div className="absolute inset-0 bg-gradient-to-br from-success/20 via-success/15 to-success/10" />
        <div className="relative p-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent via-accent/90 to-accent/70 flex items-center justify-center shadow-lg">
                  <Trophy className="w-8 h-8 text-accent-foreground" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-foreground">
                    RECOMENDACIÓN: Proveedor 1
                  </h2>
                  <Badge className="bg-accent text-accent-foreground hover:bg-accent/90">
                    Mejor Opción
                  </Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-success">
                    {formatEur(savingsEur)}
                  </span>
                  <span className="text-xl text-muted-foreground">de ahorro anual</span>
                </div>
                <div className="mt-2">
                  <Badge variant="outline" className="text-lg px-3 py-1 border-success/30 text-success">
                    {formatPct(savingsPct)} menos que P2
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full lg:w-auto">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-border/50 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-4 h-4 text-success" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Ahorro Anual
                  </span>
                </div>
                <div className="text-xl font-bold text-foreground">
                  {formatEur(savingsEur)}
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-border/50 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Aportación P1
                  </span>
                </div>
                <div className="text-xl font-bold text-foreground">
                  {formatEur(p1Contribution)}
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                className="bg-card/50 backdrop-blur-sm rounded-xl p-4 border border-border/50 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-destructive" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Aportación P2
                  </span>
                </div>
                <div className="text-xl font-bold text-foreground">
                  {formatEur(p2Contribution)}
                </div>
              </motion.div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border/50">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-success" />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Beneficio adicional por facturación:
                </p>
                <p className="text-lg font-bold text-success">
                  {formatEur(billingBenefit)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}